<template>
  <step-comparator-wrapper v-if="!isExit"
                           :card-flex="currentConfig && currentConfig.card"
                           :class="[
                            $setClasses,
                            `--${hash}`,
                            {'--no-navbar': !currentConfig || !currentConfig.showTopBackButton}
                           ]"
                           :currentIndicator="currentConfig && currentConfig.nbStep"
                           :loading-text="loadingText"
                           :other-loading="somethingIsLoading"
                           :subtitle="subtitle">
    <template v-slot:content-header>
      <wrapper-navbar class="--back" v-if="currentConfig && currentConfig.showTopBackButton">
        <el-button :disabled="somethingIsLoading"
                   @click="previousStep">
          <font-awesome-icon :icon="['fas', 'angle-left']" class="fa-lg" />
          <span class="u-margin-left-small u-font-weight-bold">{{ $t('comparator.back') }}</span>
        </el-button>
      </wrapper-navbar>
      <div :key="hash"
           :class="$vClass('wrapper-header')">
        <span v-if="title"
              :class="[$vClass('title'), 'u-margin-left-small']">
          {{ title }}
        </span>
        <card-flex v-if="!loading && infoStep && infoStep.content"
                    :class="$vClass('info-step')"
                    :content="infoStep.content"
                    :icon="infoStep.icon"
                    :icon-green="true"
                    close-message
                    mobile-wrapper-column>
        </card-flex>
      </div>
    </template>
    <component v-if="hash && currentConfig"
               :is="nameComponent"
               :offer="currentOffer"
               :ref="hash"
               @update:offer="updateOffer"/>
    <template v-slot:action-before v-if="currentConfig && currentConfig.previous">
      <el-button @click="previousStep"
                 :disabled="somethingIsLoading">
        <font-awesome-icon :icon="['fas', 'angle-left']" class="fa-lg" />
        {{ previousButtonText }}
      </el-button>
    </template>
    <template v-slot:action-after v-if="currentConfig && currentConfig.next">
      <el-button @click="nextStep"
                 :disabled="currentConfig.next.disabled || somethingIsLoading"
                 :loading="somethingIsLoading || !currentConfig.next"
                 class="el-button--success">
        {{ nextButtonText }}
        <font-awesome-icon :icon="['fas', 'angle-right']" class="fa-lg" />
      </el-button>
    </template>
  </step-comparator-wrapper>
</template>

<script>
import { mapGetters } from 'vuex';
import ConfigLoader from '@/utils/components/ConfigLoader.vue';
import pathText from '@/utils/helpers/pathText';
import StepComparatorWrapper from '@/components/booking/StepComparatorWrapper.vue';
import CardFlex from '../utils/components/CardFlex.vue';
import ProceduresHelper from '../components/procedures/ProceduresHelper.vue';
import WrapperNavbar from '../utils/components/WrapperNavbar.vue';

export default {
  name: 'booking',
  nameClassStyle: 'booking',
  extends: ConfigLoader,
  components: {
    ProceduresHelper,
    StepComparatorWrapper,
    WrapperNavbar,
    CardFlex,
  },
  data() {
    return {
      refComponent: null,
      steps: ['confirm', 'payment', 'resume', 'services', 'signature'],
      syncAction: false,
      forceReloadOnCreate: false,
      forceGetOffer: true,
      syncLoading: true,
      isExit: false,
    };
  },
  computed: {
    ...mapGetters({
      comparatorConfig: 'configStore/comparatorConfig',
      currentOffer: 'configStore/offer',
      demand: 'demandStore/demand',
      loading: 'configStore/loading',
      stepLoading: 'configStore/stepLoading',
      projectIdSelected: 'projectStore/projectIdSelected',
      servicesPrice: 'serviceStore/servicesPrice',
      totalComparatorStep: 'configStore/totalComparatorStep',
      isPrebooking: 'configStore/isPrebooking',
    }),
    somethingIsLoading() {
      return this.syncLoading || this.loading || this.stepLoading;
    },
    currentConfig() {
      return this.comparatorConfig[this.hash];
    },
    title() {
      return this.currentConfig
        && this.getTextVersion(
          (this.loading && this.currentConfig.loadingTitle)
            ? this.currentConfig.loadingTitle
            : this.currentConfig.title, {
            mover: this.currentOffer
              && this.currentOffer.mover
              && this.currentOffer.mover.customName,
          },
        );
    },
    subtitle() {
      return this.currentConfig && this.getTextVersion(this.currentConfig.subtitle);
    },
    infoStep() {
      if (this.currentConfig && this.currentConfig.infoStep) {
        return {
          title: this.getTextVersion(this.currentConfig.infoStep.title),
          content: this.getTextVersion(this.currentConfig.infoStep.content),
          icon: this.currentConfig.infoStep.icon,
        };
      }
      return undefined;
    },
    loadingText() {
      return this.loading
        ? this.currentConfig && this.getTextVersion(this.currentConfig.loadingText)
        : null;
    },
    nextButtonText() {
      const text = this.currentConfig && this.currentConfig.next && this.currentConfig.next.text;
      return this.getTextVersion(text || 'base.next', this.getTextParams) || (this.$te(text) ? this.$t(text, this.getTextParams) : text);
    },
    previousButtonText() {
      const text = this.currentConfig && this.currentConfig.previous
        && this.currentConfig.previous.text;
      return this.getTextVersion(text || 'base.back') || this.$te(text) ? this.$t(text) : text;
    },
    hash() {
      // eslint-disable-next-line no-restricted-globals
      const hash = this.$route.hash || (location && location.hash);
      return hash && hash.slice(1);
    },
    nameComponent() {
      const nameComponent = this.hash.replace(/-[a-z]/g, ($) => $[1].toUpperCase())
        .replace(this.hash.charAt(0), ($) => $.toUpperCase());
      return () => import(`@/components/booking/steps/${nameComponent}.vue`).then().catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
        this.$router.push({ name: 'comparator' }).catch(() => {});
      });
    },
  },
  methods: {
    isExistStep(hash) {
      return this.steps.findIndex((name) => name === (hash || this.hash)) >= 0;
    },
    isAuthorized() {
      if (this.isPrebooking || !this.isCommissionaire) {
        // eslint-disable-next-line no-bitwise
        return this.isInitialStatus ^ (this.hash === 'confirm');
      }

      const isValidationDoneByPartner = this.projectSelected.params.partner_validates_quotes;
      return this.$helpers.offers.isValidOffer(
        this.currentOffer,
        this.isIndividual || !isValidationDoneByPartner,
      ) && (this.isFinishedStatus
        ? this.hash === 'confirm' || (this.hash === 'signature' && !this.isSigned)
        : this.hash !== 'confirm' && this.hash !== 'signature'
      );
    },
    generateAction(type, action) {
      const currentAction = this.currentConfig
        && this.currentConfig[type] && this.currentConfig[type].action;
      this.syncAction = true;
      this.refComponent = this.refComponent || (this.$refs && this.$refs[this.hash]);
      if (currentAction && this.refComponent && this.refComponent[currentAction]) {
        return this.refComponent[currentAction]();
      }
      // eslint-disable-next-line no-console
      return action ? action() : console.error('Unknown action called.');
    },
    nextStep() {
      const { next } = this.currentConfig;
      this.generateAction('next', () => {
        this.$router.push(!this.isExistStep(next.name)
          ? { name: next.name }
          : { name: 'booking', params: { id: this.$route.params.id }, hash: `#${next.name}` })
          .catch(() => {});
      });
    },
    previousStep() {
      const { previous } = this.currentConfig;
      const previousExists = this.isExistStep(previous.name);
      if (!previousExists) {
        this.isExit = true;
      }
      this.generateAction('previous', () => {
        this.$router.push(!previousExists
          ? { name: previous.name }
          : { name: 'booking', params: { id: this.$route.params.id }, hash: `#${previous.name}` })
          .catch(() => {});
      });
    },
    getTextVersion(text, params) {
      return pathText.getTextVersion(text, {
        ...params,
        isIndividual: this.isIndividual,
        arrhesPercent: this.arrhesPercent,
        totalSteps: this.totalComparatorStep,
        currentStep: this.currentConfig && this.currentConfig.nbStep,
      });
    },
    updateOffer(offer) {
      this.$store.commit('configStore/SET_OFFER', offer);
    },
    checkAuthorization() {
      if (this.$route.name === 'booking'
        && !this.syncAction
        && (!this.isExistStep()
          || !this.currentConfig
          || !this.currentOffer
          || !this.demand
          || !this.isAuthorized())) {
        this.isExit = true;
        this.$router.push({ name: 'comparator' }).catch(() => {});
        return false;
      }
      return true;
    },
  },
  watch: {
    hash: {
      handler() {
        if (this.checkAuthorization()) {
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
          try {
            this.loadConfig();
          // eslint-disable-next-line no-empty
          } catch (e) {}
        }
      },
    },
    currentConfig: {
      deep: true,
      handler() {
        this.refComponent = this.$refs && this.$refs[this.hash];
      },
    },
    isLoading() {
      if (!this.isLoading) {
        setTimeout(() => {
          this.syncLoading = this.isLoading;
        }, 200);
      } else {
        this.syncLoading = this.isLoading;
      }
    },
    projectSelected(newValue, old) {
      if (old.id !== newValue.id) {
        this.$router.push({ name: 'comparator' }).catch(() => {});
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.content-page__title) {
  margin: 0;
  flex-direction: column;
}

.--no-navbar :deep(.content-page__title) {
  margin-top: -60px;
  @include breakpoint(tablet) {
    margin-top: 20px;
  }
}

.booking {

  &__info-step {
    border-radius: 0;
    display: none;
    @include breakpoint(tablet) {
      border-radius: var(--nxt-border-radius-base);
      display: block;
    }
  }

  &__title {
    display: block;
    color: var(--nxt-color-white);
    font-size: var(--nxt-font-size-large);
    font-weight: var(--nxt-font-weight-bold);
    margin-top: var(--nxt-space-medium);
    margin-bottom: var(--nxt-space-medium);
    text-transform: uppercase;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    @include breakpoint(tablet) {
      margin-top: inherit;
    }
  }
}
</style>
